import type {
  PorteiroCanal,
  PorteiroCobertura,
  PorteiroDecisao,
  PorteiroDesfecho,
  PorteiroEntrega,
  PorteiroEventoTipo,
  PorteiroFiltros,
  PorteiroLotesConsulta,
  PorteiroObservabilidadeConsulta,
  PorteiroObservabilidadeParams,
  PorteiroOrigem,
  PorteiroPeriodo,
  PorteiroResultadoDaRevisao,
} from '../types/porteiroObservabilidade';

export const FILTROS_INICIAIS: PorteiroFiltros = {
  periodo: '30d',
  empresaId: 'all',
  escritorioId: 'all',
  loteId: 'all',
  canal: 'all',
  decisao: 'all',
  desfecho: 'all',
  busca: '',
};

/** Teto do contrato do core para `busca`: acima disso a rota devolve 400. */
export const TAMANHO_MAXIMO_DA_BUSCA = 100;

export const ROTULOS_PERIODO: Record<PorteiroPeriodo, string> = {
  '7d': 'Últimos 7 dias',
  '30d': 'Últimos 30 dias',
  all: 'Todo o período',
};

export const ROTULOS_CANAL: Record<PorteiroCanal, string> = {
  WHATSAPP: 'WhatsApp',
  EMAIL: 'E-mail',
};

export const ROTULOS_ORIGEM: Record<PorteiroOrigem, string> = {
  NOTIFICACAO: 'Notificação',
  NEGOCIACAO: 'Negociação',
};

export const ROTULOS_DECISAO: Record<PorteiroDecisao, string> = {
  LIBERADO: 'Liberado',
  BLOQUEADO: 'Barrado',
  COM_AVISO: 'Com aviso',
  NAO_VERIFICADO: 'Não verificado',
};

export const ROTULOS_DESFECHO: Record<PorteiroDesfecho, string> = {
  CORRIGIDO: 'Corrigido',
  IGNORADO: 'Ignorado',
  NAO_PEGO: 'Não pego confirmado',
  SUSPEITA: 'Sob investigação',
};

export const ROTULOS_ENTREGA: Record<PorteiroEntrega, string> = {
  ENTREGUE: 'Entregue',
  FALHOU_DESTINATARIO: 'Falha atribuída ao destinatário',
  FALHOU_OUTRO_MOTIVO: 'Falha por outro motivo',
  NAO_ENVIADO: 'Não enviado',
  PENDENTE: 'Aguardando entrega',
};

export const ROTULOS_EVENTO: Record<PorteiroEventoTipo, string> = {
  CORRECAO: 'Contato corrigido pelo usuário',
  CONTATO_ALTERADO: 'Contato trocado, ainda barrado ou sem verificação',
  IGNORADO: 'Pendência ignorada pelo usuário',
  IGNORADO_REVERTIDO: 'Ignorar revertido',
  SUSPEITA_ABERTA: 'Suspeita de escape aberta pela falha de entrega',
  ESCAPE_REVISADO: 'Suspeita revisada',
};

export const ROTULOS_RESULTADO_DA_REVISAO: Record<PorteiroResultadoDaRevisao, string> = {
  CONFIRMADO: 'Confirmar: o porteiro deixou passar',
  DESCARTADO: 'Descartar: não foi falha do porteiro',
};

/** Teto do core para a justificativa e a evidência da revisão (acima disso, 400). */
export const TAMANHO_DA_JUSTIFICATIVA = { minimo: 10, maximo: 2000 } as const;
export const TAMANHO_MAXIMO_DA_EVIDENCIA = 500;

/** Lotes por página no filtro, o padrão do `GET /lotes`. */
export const LOTES_POR_PAGINA = 30;

const FILTROS_DE_SELECAO = [
  'empresaId',
  'escritorioId',
  'loteId',
  'canal',
  'decisao',
  'desfecho',
] as const satisfies readonly (keyof PorteiroFiltros)[];

/**
 * Query do `GET /admin/platform/porteiro/observabilidade`. Nos selects, `'all'` é o "sem filtro"
 * da tela e não vai ao servidor. O período é a exceção: `periodo=all` é valor do contrato, e
 * omiti-lo faria o servidor aplicar o padrão de 30 dias no lugar de "Todo o período".
 */
export function paramsDaConsulta({
  filtros,
  cursor,
}: PorteiroObservabilidadeConsulta): PorteiroObservabilidadeParams {
  const params: PorteiroObservabilidadeParams = { periodo: filtros.periodo };
  for (const campo of FILTROS_DE_SELECAO) {
    const valor = filtros[campo];
    if (valor !== 'all') params[campo] = valor;
  }
  const busca = filtros.busca.trim();
  if (busca) params.busca = busca;
  if (cursor) params.cursor = cursor;
  return params;
}

/** Query do `GET /admin/platform/porteiro/lotes`; busca vazia não vai. */
export function paramsDosLotes({
  escritorioId,
  busca,
  page,
  limit,
}: PorteiroLotesConsulta): PorteiroObservabilidadeParams {
  const params: PorteiroObservabilidadeParams = {
    escritorioId,
    page: String(page),
    limit: String(limit),
  };
  const termo = busca?.trim();
  if (termo) params.busca = termo;
  return params;
}

/**
 * Taxa da cobertura em %, ou `null` quando não há elegível para dividir. Arredonda para baixo e,
 * com qualquer lacuna (sem trilha ou lote sem conclusão), nunca mostra 100%: 999 de 1000 arredondado
 * para cima afirmaria uma cobertura completa que não existe.
 */
export function taxaDaCobertura(cobertura: PorteiroCobertura): number | null {
  if (cobertura.elegiveis === 0) return null;
  const taxa = Math.floor((cobertura.comDecisaoRegistrada / cobertura.elegiveis) * 100);
  const temLacuna = cobertura.semTrilha > 0 || cobertura.lotesSemConclusao > 0;
  return Math.min(taxa, temLacuna ? 99 : 100);
}

const CANAIS = Object.keys(ROTULOS_CANAL);
const DECISOES = Object.keys(ROTULOS_DECISAO);
const DESFECHOS = Object.keys(ROTULOS_DESFECHO);
const PERIODOS = Object.keys(ROTULOS_PERIODO);

export function ehPorteiroCanal(valor: string): valor is PorteiroCanal {
  return CANAIS.includes(valor);
}

export function ehPorteiroDecisao(valor: string): valor is PorteiroDecisao {
  return DECISOES.includes(valor);
}

export function ehPorteiroDesfecho(valor: string): valor is PorteiroDesfecho {
  return DESFECHOS.includes(valor);
}

export function ehPorteiroPeriodo(valor: string): valor is PorteiroPeriodo {
  return PERIODOS.includes(valor);
}
